import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "px-4 sm:px-0" }
const _hoisted_2 = {
  key: 0,
  class: "px-4 pt-6 mb-3 first:mt-0 first:pt-4"
}
const _hoisted_3 = { class: "text-[11px] font-bold uppercase tracking-[0.15em] text-gray-400" }
const _hoisted_4 = {
  key: 1,
  class: "flex flex-col gap-y-3"
}
const _hoisted_5 = {
  key: 2,
  class: "flex flex-col gap-y-3"
}
const _hoisted_6 = {
  key: 3,
  class: "flex flex-col gap-y-3"
}
const _hoisted_7 = {
  key: 4,
  class: "flex flex-col gap-y-3"
}
const _hoisted_8 = { class: "mt-12 pb-8" }
const _hoisted_9 = { class: "text-xl font-bold text-gray-900 mb-2 uppercase tracking-wider" }
const _hoisted_10 = { class: "text-sm text-gray-500 mb-8 leading-relaxed" }
const _hoisted_11 = { class: "flex flex-col gap-3" }

import {computed, inject, reactive, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import AppModal from "@/components/appModal.vue";
import {useStore} from "vuex";
import ItemV3ConfAi from "@/components/itemV3/itemV3ConfAi.vue";
import ItemV3DiscsColor from "@/components/itemV3/parameter/itemV3DiscsColor.vue";
import ItemV3DiscsFinish from "@/components/itemV3/parameter/itemV3DiscsFinish.vue";
import ItemV3Profile from "@/components/itemV3/parameter/itemV3Profile.vue";
import ItemV3Height from "@/components/itemV3/parameter/itemV3Height.vue";
import ItemV3Width from "@/components/itemV3/parameter/itemV3Width.vue";
import ItemV3Stones from "@/components/itemV3/parameter/itemV3Stones.vue";
import ItemV3DiscsMetal from "@/components/itemV3/parameter/itemV3DiscsMetal.vue";
import ItemV3DiscsRatio from "@/components/itemV3/parameter/itemV3DiscsRatio.vue";
import ItemV3Grooves from "@/components/itemV3/parameter/itemV3Grooves.vue";
import ItemV3Engraving from "@/components/itemV3/parameter/itemV3Engraving.vue";

const pageStr = "components.itemV2.itemV2VariantsConf"

const IMAGES_RESIZE_HOST = "conf.images.rubin.jewelry";

// Size (in pixels) appended to image URLs served from IMAGES_RESIZE_HOST.

export default /*@__PURE__*/_defineComponent({
  __name: 'itemV3VariantsConf',
  props: ["item", "sessionId"],
  emits: ["patch", "hover"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const props = __props;

const store = useStore();
const {t} = useI18n();
const loadItemVariants = inject<any>("loadItemVariants");
const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");
const visibleVariants = reactive<Set<string>>(new Set());

// Whether the current item is a wedding ring - the horizontal-tabs/vertical-groups layout
// switch below is only offered for wedding rings.
const isWeddingRing = computed(() => props.item?.facts?.item_type === 'wedding_ring');

// Variants controlling the stone type (earth/lab), rendered as a switch button inside the
// "Stones" group (see template) instead of a fixed position outside the configurator.
const stoneTypes = computed(() => {
  const types: any[] = [];
  if (props.item && props.item.variants) {
    for (const variantIdx in props.item.variants) {
      const variantData = props.item.variants[variantIdx];
      if (variantData?.idx?.includes("stone_type")) {
        types.push(variantData);
      }
    }
  }
  return types;
});

// Width of the item's facts - exposed to the extracted itemV2VariantValuesList/
// itemV2GrooveType sub components since they can't reliably read "item" from this
// component's own prop within templates where "item" is shadowed by the section.items
// v-for loop variable (see template above).
const itemWidth = computed(() => props.item?.facts?.width);
const itemMetal = computed(() => props.item?.image?.main_metal);
const itemProfile = computed(() => props.item?.facts?.profile);

// Whether "set view" (both items of a set are configured together) is currently active.
const configureAll = computed(() => store.getters["session/getConfigureAll"]);

// All items of the current session - used in set-view mode to load and compare variants
// across items instead of only the currently selected one.
const allItems = computed(() => session?.value?.items || []);

// Variant idx's that are never used/displayed while set-view is active (e.g. the ring size,
// which is inherently individual per item and therefore not part of the shared configuration).
const setViewHiddenVariantIdxs = ['size'];

// Loads the values of a variant for every relevant item: all items when set-view is active,
// otherwise only the currently selected item. Mirrors the lazy-loading behaviour that was
// previously hard-coded to the single selected item.
const ensureVariantLoaded = (variantIdx: string) => {
  if (!loadItemVariants) return;
  if (configureAll.value) {
    allItems.value.forEach((otherItem: any, idx: number) => {
      const variantData = (otherItem.variants || []).find((v: any) => v.idx === variantIdx);
      if (variantData && variantData.values.length === 0) {
        loadItemVariants(idx, variantIdx);
      }
    });
  } else if (itemIdx) {
    const variantData = props.item.variants.find((v: any) => v.idx === variantIdx);
    if (variantData && variantData.values.length === 0) {
      loadItemVariants(itemIdx.value, variantIdx);
    }
  }
};

// Sorted list of value idx's of a variant - used to compare whether a variant is "equal"
// (i.e. offers the exact same options) across all items of a set.
const valueIdxsOf = (variant: any) => (variant?.values || []).map((v: any) => v.idx).slice().sort();

// In set-view mode, only variants that exist on every item AND - once their values are
// loaded - offer the exact same values (compared via values[].idx) are shown. Returns null
// when set-view is not active, meaning no filtering should be applied.
const commonVariantIdxs = computed<string[] | null>(() => {
  if (!configureAll.value || allItems.value.length < 2) return null;

  let common: string[] | null = null;
  allItems.value.forEach((otherItem: any) => {
    const idxs = (otherItem.variants || [])
        .filter((v: any) => !setViewHiddenVariantIdxs.includes(v.idx))
        .map((v: any) => v.idx);
    common = common === null ? idxs : common.filter((idx: string) => idxs.includes(idx));
  });
  if (!common) return common;

  return common.filter((idx: string) => {
    let referenceValues: string[] | null = null;
    return allItems.value.every((otherItem: any) => {
      const variant = (otherItem.variants || []).find((v: any) => v.idx === idx);
      if (!variant) return false;
      if (!variant.values || variant.values.length === 0) return true; // not loaded yet, keep tentatively
      const values = valueIdxsOf(variant);
      if (referenceValues === null) {
        referenceValues = values;
        return true;
      }
      return JSON.stringify(referenceValues) === JSON.stringify(values);
    });
  });
});

// Counts the number of distinct discs an item has, based on its "discs.<n>.width"
// variants (kept even for single-value discs, see itemV2Main.vue) - mirrors the disc
// grouping logic in itemV2DiscSeparationConfigurator.vue/itemV2DiscWidthConfigurator.vue.
const discsCount = (itemVariants: any): number => {
  const discIndexes = new Set<number>();
  for (const key in itemVariants || {}) {
    const variant = itemVariants[key];
    if (!variant?.idx) continue;
    const parts = variant.idx.split('.');
    if (parts[0] !== 'discs' || parts.length < 3 || parts[2] !== 'width') continue;
    const discIndex = parseInt(parts[1], 10);
    if (!isNaN(discIndex)) discIndexes.add(discIndex);
  }
  return discIndexes.size;
};

// While set-view is active, the disc separation suggestions only make sense if every
// item in the set has the same number of discs - otherwise the ratio presets (e.g. "1:2")
// would not apply consistently across items, so the configurator is hidden entirely.
const discsCountsDifferAcrossSet = computed(() => {
  if (!configureAll.value || allItems.value.length < 2) return false;
  const counts = allItems.value.map((otherItem: any) => discsCount(otherItem.variants));
  return new Set(counts).size > 1;
});

const observeVariant = (el: any, variantIdx: string) => {
  if (el && loadItemVariants && itemIdx && variantIdx) {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        visibleVariants.add(variantIdx);
        ensureVariantLoaded(variantIdx);
      } else {
        visibleVariants.delete(variantIdx);
      }
    }, {
      rootMargin: '400px 0px',
      threshold: 0.01
    });
    observer.observe(el);
  }
};

const observeGroup = (el: any, variantsList: any[]) => {
  if (el && loadItemVariants && itemIdx) {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        variantsList.forEach(variant => {
          if (!variant.idx) return;
          visibleVariants.add(variant.idx);
          ensureVariantLoaded(variant.idx);
        });
      } else {
        variantsList.forEach(variant => {
          visibleVariants.delete(variant.idx);
        });
      }
    }, {
      rootMargin: '400px 0px',
      threshold: 0.01
    });
    observer.observe(el);
  }
};

watch([() => props.item.variants, allItems], () => {
  visibleVariants.forEach(vIdx => ensureVariantLoaded(vIdx));
}, {deep: true});

// Host serving item images that support on-the-fly resizing via a trailing size segment.
const imageSize = ref(500);

// If the given URL is served from IMAGES_RESIZE_HOST, appends the current image_size
// at the end of the URL (e.g. ".../<hash>" -> ".../<hash>/500").
const applyImageSize = (url: string) => {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    if (parsed.hostname === IMAGES_RESIZE_HOST) {
      return url.replace(/\/+$/, '') + '/' + imageSize.value;
    }
  } catch {
    // not an absolute URL, leave it untouched
  }
  return url;
};

// Resolves the main image url from a variant value's image (string or object) and,
// if it comes from IMAGES_RESIZE_HOST, appends the configured image_size to it.
const getImageUrl = (image: any) => {
  const url = typeof image === 'string' ? image : image?.filename;
  return applyImageSize(url);
};

// Resolves the layer image url from a variant value's image (object only) and,
// if it comes from IMAGES_RESIZE_HOST, appends the configured image_size to it.
const getImageLayerUrl = (image: any) => {
  const url = typeof image === 'object' ? image?.filename_layer : '';
  return applyImageSize(url);
};

const variants = computed(() => {
  let variants: any = {};
  if (props.item && props.item.variants) {
    for (let variantIdx in props.item.variants) {
      const variant = props.item.variants[variantIdx];
      if (variant) {
        variants[variant.idx] = variant;
      }
    }
  }
  return variants;
});

const variantsListAll = computed(() => {
  let listOfVariants: any[] = [];

  if (props.item && props.item.variants) {
    for (let variantIdx in props.item.variants) {
      const variant = props.item.variants[variantIdx];
      if (!variant) continue;
      if (isWeddingRing.value && variant.idx?.includes('stone_type') && !variant.idx.startsWith('stones.')) continue;
      if (configureAll.value && setViewHiddenVariantIdxs.includes(variant.idx)) continue;
      if (commonVariantIdxs.value !== null && !commonVariantIdxs.value.includes(variant.idx)) continue;
      if (Object.keys(variant.conf_groups).length !== 0) {
        for (let conf_group_idx in variant.conf_groups) {
          listOfVariants.push({
            idx: variant.idx,
            group: variant.group?.title || "",
            order: variant.order,
            prio: variant.prio,
            image_type: variant.image_type,
            conf_group_idx: conf_group_idx,
            conf_groups: variant.conf_groups,
            conf_groups_title: variant.conf_groups_title,
            title: variant.title,
          });
        }
      } else {
        listOfVariants.push({
          idx: variant.idx,
          group: variant.group?.title || "",
          order: variant.order,
          prio: variant.prio,
          conf_group_idx: "",
          image_type: variant.image_type,
          conf_groups: variant.conf_groups,
          conf_groups_title: variant.conf_groups_title,
          title: variant.title,
        });
      }
    }
  }

  // Sort: first by prio (those with prio != -1 first, sorted by prio), then by order
  listOfVariants.sort((a: any, b: any) => {
    if (a.prio !== -1 && b.prio === -1) return -1;
    if (a.prio === -1 && b.prio !== -1) return 1;
    if (a.prio !== -1 && b.prio !== -1) return a.prio - b.prio;
    return a.order - b.order;
  });

  return listOfVariants;
});

// Fixed groups rendered below, in the exact order their parameter components should
// appear. This intentionally ignores the previous automatic grouping derived from the
// item's variant idx's (see groupedVariants/tabsList history) in favour of an explicit,
// hand-picked list of components per group.
const sectionsToRender = computed(() => [
  {id: 'ringform', title: t(pageStr + '.group_ringform')},
  {id: 'design', title: t(pageStr + '.group_design')},
  {id: 'stones', title: t(pageStr + '.group_stones')},
  {id: 'personal', title: t(pageStr + '.group_personal')},
]);

const getVariantValues = (menuVariant: any) => {
  const variantData = variants.value[menuVariant.idx];
  if (!variantData) return [];

  const finaleValues = [];
  const conf_group_idx = menuVariant.conf_group_idx;
  const usedSettings: string[] = [];

  for (let idx = 0; idx < variantData.values.length; idx++) {
    const value = variantData.values[idx];

    if (conf_group_idx !== "") {
      if (conf_group_idx == "model") {
        if (
            menuVariant.conf_groups.setting == value.conf_groups.setting &&
            menuVariant.conf_groups.size == value.conf_groups.size
        ) {
          finaleValues.push(value);
        }
      } else if (conf_group_idx == "size") {
        if (
            menuVariant.conf_groups.model == value.conf_groups.model &&
            menuVariant.conf_groups.setting == value.conf_groups.setting
        ) {
          finaleValues.push(value);
        }
      } else if (conf_group_idx == "setting") {
        if (
            (menuVariant.conf_groups.setting == value.conf_groups.setting &&
                value.match == true) ||
            (menuVariant.conf_groups.setting !== value.conf_groups.setting &&
                !usedSettings.includes(value.conf_groups.setting))
        ) {
          usedSettings.push(value.conf_groups.setting);
          finaleValues.push(value);
        }
      }
    } else {
      if (
          !(
              variantData.idx?.includes("side_stones_id") &&
              value.used_dependencies.length > 0
          )
      ) {
        finaleValues.push(value);
      }
    }
  }
  return finaleValues;
};

const handleDirectPatch = (variant: any) => {
  const values = getVariantValues(variant);

  let value = ""
  let certificated_diamond = ""

  if (values.length > 0) {
    value = values[0]?.idx || "";
    certificated_diamond = values[0]?.certificated_diamond || "";
  } else if (variant.idx?.includes(".remove")) {
    value = "remove"
  } else if (variant.idx?.includes(".add")) {
    value = "add"
  }

  emit('patch', {
    action: variant.idx,
    value: value,
    certificated_diamond: certificated_diamond
  });
};

const showRemoveModal = ref(false);
const variantToRemove = ref<any>(null);

const confirmRemove = (variant: any) => {
  variantToRemove.value = variant;
  showRemoveModal.value = true;
};

const handleRemove = () => {
  if (variantToRemove.value) {
    handleDirectPatch(variantToRemove.value);
    showRemoveModal.value = false;
    variantToRemove.value = null;
  }
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sectionsToRender.value, (section) => {
      return (_openBlock(), _createElementBlock(_Fragment, {
        key: 'section-' + section.id
      }, [
        (sectionsToRender.value.length > 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("span", _hoisted_3, _toDisplayString(section.title), 1)
            ]))
          : _createCommentVNode("", true),
        (section.id === 'ringform')
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(ItemV3Profile, {
                item: __props.item,
                onPatch: _cache[0] || (_cache[0] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"]),
              _createVNode(ItemV3Width, {
                item: __props.item,
                onPatch: _cache[1] || (_cache[1] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"]),
              _createVNode(ItemV3Height, {
                item: __props.item,
                onPatch: _cache[2] || (_cache[2] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"])
            ]))
          : _createCommentVNode("", true),
        (section.id === 'design')
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(ItemV3DiscsRatio, {
                item: __props.item,
                onPatch: _cache[3] || (_cache[3] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"]),
              _createVNode(ItemV3DiscsMetal, {
                item: __props.item,
                onPatch: _cache[4] || (_cache[4] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"]),
              _createVNode(ItemV3DiscsColor, {
                item: __props.item,
                onPatch: _cache[5] || (_cache[5] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"]),
              _createVNode(ItemV3Grooves, {
                item: __props.item,
                onPatch: _cache[6] || (_cache[6] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"]),
              _createVNode(ItemV3DiscsFinish, {
                item: __props.item,
                onPatch: _cache[7] || (_cache[7] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"])
            ]))
          : _createCommentVNode("", true),
        (section.id === 'stones')
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(ItemV3Stones, {
                item: __props.item,
                onPatch: _cache[8] || (_cache[8] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"])
            ]))
          : _createCommentVNode("", true),
        (section.id === 'personal')
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createVNode(ItemV3Engraving, {
                item: __props.item,
                onPatch: _cache[9] || (_cache[9] = (event) => _ctx.$emit('patch', event))
              }, null, 8, ["item"])
            ]))
          : _createCommentVNode("", true)
      ], 64))
    }), 128)),
    _createElementVNode("div", _hoisted_8, [
      false
        ? (_openBlock(), _createBlock(ItemV3ConfAi, {
            key: 0,
            title: _ctx.$t(pageStr + '.rubi_assistant'),
            subtitle: _ctx.$t(pageStr + '.chat_with_our_ai_stylist')
          }, null, 8, ["title", "subtitle"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(AppModal, {
      modelValue: showRemoveModal.value,
      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((showRemoveModal).value = $event)),
      label: _ctx.$t('components.itemV2.itemV2Main.remove_feature_confirm_title')
    }, {
      default: _withCtx(() => [
        _createVNode(_Transition, { name: "fade-fast" }, {
          default: _withCtx(() => [
            (showRemoveModal.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-6",
                  onClick: _cache[12] || (_cache[12] = ($event: any) => (showRemoveModal.value = false))
                }, [
                  _createElementVNode("div", {
                    class: "bg-white rounded-[2.5rem] p-8 lg:p-12 max-w-sm w-full shadow-2xl animate-scale-in",
                    onClick: _cache[11] || (_cache[11] = _withModifiers(() => {}, ["stop"]))
                  }, [
                    _cache[14] || (_cache[14] = _createElementVNode("div", { class: "w-16 h-16 rounded-full bg-brand-main/10 flex items-center justify-center text-brand-main mb-6" }, [
                      _createElementVNode("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        class: "h-8 w-8",
                        viewBox: "0 0 24 24",
                        fill: "none",
                        stroke: "currentColor",
                        "stroke-width": "2.5",
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round"
                      }, [
                        _createElementVNode("polyline", { points: "3 6 5 6 21 6" }),
                        _createElementVNode("path", { d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" })
                      ])
                    ], -1)),
                    _createElementVNode("h3", _hoisted_9, _toDisplayString(_ctx.$t('components.itemV2.itemV2Main.remove_feature_confirm_title')), 1),
                    _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t('components.itemV2.itemV2Main.remove_feature_confirm_text')), 1),
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("button", {
                        type: "button",
                        onClick: handleRemove,
                        class: "w-full bg-brand-main text-white py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-brand-main/90 transition-all active:scale-95 shadow-lg shadow-brand-main/20"
                      }, _toDisplayString(_ctx.$t('components.itemV2.itemV2ItemWidget.confirm')), 1),
                      _createElementVNode("button", {
                        type: "button",
                        onClick: _cache[10] || (_cache[10] = ($event: any) => (showRemoveModal.value = false)),
                        class: "w-full bg-gray-50 text-gray-400 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-100 hover:text-gray-900 transition-all active:scale-95"
                      }, _toDisplayString(_ctx.$t('components.itemV2.itemV2ItemWidget.cancel')), 1)
                    ])
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue", "label"])
  ]))
}
}

})